import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNotificationStore } from "@/store/useNotificationStore";
import { loadScratchPadState, saveScratchPadState } from "./storage";
import {
  ScratchPadImageItem,
  ScratchPadMode,
  ScratchPadState,
  ScratchPadStroke,
  ScratchPadTextItem,
  ScratchPadTool,
} from "./types";

/** Where a swept stroke sat, so an undo can put it back there. Ascending. */
type ErasedStroke = Readonly<{ stroke: ScratchPadStroke; index: number }>;

type UndoAction =
  | { kind: "stroke"; stroke: ScratchPadStroke }
  | { kind: "eraseMany"; entries: readonly ErasedStroke[] }
  | { kind: "text"; id: string };

type RedoAction =
  | { kind: "stroke"; stroke: ScratchPadStroke }
  | { kind: "eraseMany"; entries: readonly ErasedStroke[] }
  | { kind: "text"; item: ScratchPadTextItem };

type ScratchPadScope = Readonly<{
  sessionId?: string | null;
  slideIndex?: number | string | null;
}>;

export const useScratchPad = ({ sessionId, slideIndex }: ScratchPadScope) => {
  const initial = useMemo(
    () => loadScratchPadState(sessionId, slideIndex),
    [sessionId, slideIndex],
  );
  const [mode, setMode] = useState<ScratchPadMode>("draw");
  const [tool, setTool] = useState<ScratchPadTool>("pen");
  const [penColor, setPenColor] = useState("#000000");
  const [penSize, setPenSize] = useState(4);
  const [eraserSize, setEraserSize] = useState(18);
  const [strokes, setStrokes] = useState<readonly ScratchPadStroke[]>(
    initial.strokes,
  );
  const [textItems, setTextItems] = useState<readonly ScratchPadTextItem[]>(
    initial.textItems ?? [],
  );
  const [imageItems, setImageItems] = useState<readonly ScratchPadImageItem[]>(
    initial.imageItems ?? [],
  );
  const [activeTextId, setActiveTextId] = useState<string | null>(null);
  const undoRef = useRef<readonly UndoAction[]>([]);
  const redoRef = useRef<readonly RedoAction[]>([]);

  const state: ScratchPadState = useMemo(
    () => ({ strokes, textItems, imageItems }),
    [strokes, textItems, imageItems],
  );

  useEffect(() => {
    setStrokes(initial.strokes);
    setTextItems(initial.textItems ?? []);
    setImageItems(initial.imageItems ?? []);
    undoRef.current = [];
    redoRef.current = [];
    setActiveTextId(null);
  }, [initial.strokes, initial.textItems, initial.imageItems]);

  const { showNotification } = useNotificationStore();
  useEffect(() => {
    const saved = saveScratchPadState(sessionId, slideIndex, state);
    if (!saved) {
      showNotification(
        "Could not save scratchpad — storage is full",
        undefined,
        4000,
      );
    }
  }, [sessionId, slideIndex, state, showNotification]);

  const strokeStyle = useMemo(
    () => ({
      tool,
      color: tool === "pen" ? penColor : "#000000",
      size: tool === "pen" ? penSize : eraserSize,
    }),
    [eraserSize, penColor, penSize, tool],
  );

  // The space-bar draw lock always writes with the pen, whatever tool the
  // toolbar shows — the same "space means pen" the tldraw surfaces get from
  // stash-tool → setCurrentTool("draw") → restore. The scratchpad has no tool
  // stack to swap, so the lock is handed the pen template directly instead.
  const penStrokeStyle = useMemo(
    () => ({ tool: "pen" as const, color: penColor, size: penSize }),
    [penColor, penSize],
  );

  const pushStroke = (stroke: ScratchPadStroke) => {
    redoRef.current = [];
    undoRef.current = [...undoRef.current, { kind: "stroke", stroke }];
    setStrokes((prev) => [...prev, stroke]);
  };

  const removeStrokes = (targets: readonly ScratchPadStroke[]) => {
    if (targets.length === 0) return;
    redoRef.current = [];
    setStrokes((prev) => {
      const drop = new Set(targets);
      // Positions are resolved here, against the array actually being written,
      // rather than carried from wherever the sweep was gathered: an undo or a
      // clear in between would have renumbered everything under it.
      const entries = prev
        .map((stroke, index) => ({ stroke, index }))
        .filter((entry) => drop.has(entry.stroke));
      if (entries.length === 0) return prev;
      // One entry for the whole sweep, so a drag undoes in a single tap.
      undoRef.current = [...undoRef.current, { kind: "eraseMany", entries }];
      return prev.filter((stroke) => !drop.has(stroke));
    });
  };

  const undo = () => {
    const action = undoRef.current[undoRef.current.length - 1];
    if (!action) return;
    undoRef.current = undoRef.current.slice(0, -1);
    if (action.kind === "stroke") {
      setStrokes((prev) => {
        const index = prev.lastIndexOf(action.stroke);
        if (index < 0) return prev;
        redoRef.current = [
          ...redoRef.current,
          { kind: "stroke", stroke: action.stroke },
        ];
        return prev.filter((_, i) => i !== index);
      });
      return;
    }
    if (action.kind === "eraseMany") {
      setStrokes((prev) => {
        redoRef.current = [...redoRef.current, action];
        const next = [...prev];
        // Ascending: each insert has to land before the ones above it shift.
        action.entries.forEach(({ stroke, index }) => {
          next.splice(Math.min(index, next.length), 0, stroke);
        });
        return next;
      });
      return;
    }
    setTextItems((prev) => {
      const item = prev.find((entry) => entry.id === action.id);
      if (!item) return prev;
      redoRef.current = [...redoRef.current, { kind: "text", item }];
      return prev.filter((entry) => entry.id !== action.id);
    });
  };

  const redo = () => {
    const action = redoRef.current[redoRef.current.length - 1];
    if (!action) return;
    redoRef.current = redoRef.current.slice(0, -1);
    if (action.kind === "stroke") {
      undoRef.current = [
        ...undoRef.current,
        { kind: "stroke", stroke: action.stroke },
      ];
      setStrokes((prev) => [...prev, action.stroke]);
      return;
    }
    if (action.kind === "eraseMany") {
      setStrokes((prev) => {
        const drop = new Set(action.entries.map((entry) => entry.stroke));
        const entries = prev
          .map((stroke, index) => ({ stroke, index }))
          .filter((entry) => drop.has(entry.stroke));
        if (entries.length === 0) return prev;
        undoRef.current = [...undoRef.current, { kind: "eraseMany", entries }];
        return prev.filter((stroke) => !drop.has(stroke));
      });
      return;
    }
    undoRef.current = [
      ...undoRef.current,
      { kind: "text", id: action.item.id },
    ];
    setTextItems((prev) => [...prev, action.item]);
  };

  const clear = useCallback(() => {
    undoRef.current = [];
    redoRef.current = [];
    setStrokes([]);
    setTextItems([]);
    setImageItems([]);
    setActiveTextId(null);
  }, []);

  const createId = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const addTextItem = () => {
    const id = createId();
    const item = { id, text: "", x: 0.08, y: 0.06, w: 0.78 };
    redoRef.current = [];
    undoRef.current = [...undoRef.current, { kind: "text", id }];
    setTextItems((prev) => [...prev, item]);
    setActiveTextId(id);
    setMode("text");
  };

  const updateTextItem = (id: string, text: string) =>
    setTextItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, text } : item)),
    );

  const moveTextItem = (id: string, x: number, y: number) =>
    setTextItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, x, y } : item)),
    );

  const removeTextItem = (id: string) =>
    setTextItems((prev) => prev.filter((item) => item.id !== id));

  const addImageItem = (dataUrl: string): void => {
    const id = createId();
    const name = `Image ${imageItems.length + 1}`;
    const item: ScratchPadImageItem = { id, name, dataUrl };
    setImageItems((prev) => [...prev, item]);
  };

  const clearImageBytes = useCallback(() => {
    setImageItems((prev) =>
      prev.some((item) => item.dataUrl)
        ? prev.map((item) => (item.dataUrl ? { ...item, dataUrl: "" } : item))
        : prev,
    );
  }, []);

  const removeImageItem = (id: string) =>
    setImageItems((prev) => prev.filter((item) => item.id !== id));

  return {
    mode,
    setMode,
    tool,
    setTool,
    penColor,
    setPenColor,
    penSize,
    setPenSize,
    eraserSize,
    setEraserSize,
    strokes,
    pushStroke,
    removeStrokes,
    undo,
    redo,
    clear,
    textItems,
    addTextItem,
    updateTextItem,
    moveTextItem,
    removeTextItem,
    activeTextId,
    setActiveTextId,
    imageItems,
    addImageItem,
    removeImageItem,
    clearImageBytes,
    strokeStyle,
    penStrokeStyle,
  };
};
